import React, { useState } from 'react';
import { TaskItem } from '../../../types/tasks';
import { useTasks } from '../../../context/TasksContext';
import { relativeDay } from '../../../utils/taskDates';
import { Flag } from '../../../utils/projectDashboard';
import ProgressCheck from '../../tasks/ProgressCheck';
import TaskIcon, { TaskIconName } from '../../tasks/TaskIcon';
import { BubbleStack } from '../../tasks/UserBubble';

/** One line on a dashboard card: tick it, open it, see why it's there. */
export const DashRow: React.FC<{
  item: TaskItem;
  flag?: { label: string; tone: Flag['tone']; date?: string | null };
  /** Where it sits: "Project › Branch" (see whereLabel). */
  context?: string;
  readOnly?: boolean;
  onOpen: (item: TaskItem) => void;
}> = ({ item, flag, context, readOnly, onOpen }) => {
  const { users, today, updateItem } = useTasks();
  const done = item.progress >= 100;
  return (
    <li className={`tp-d-row${done ? ' is-done' : ''}`}>
      <ProgressCheck
        value={item.progress}
        onToggle={() => !readOnly && updateItem(item.id, { progress: done ? 0 : 100 })}
        disabled={readOnly}
        label={`${done ? 'Uncheck' : 'Check'} ${item.text}`}
        size={18}
      />
      <button type="button" className="tp-d-name" onClick={() => onOpen(item)} title="Open it">
        <span>{item.text || 'Untitled'}</span>
        {context && <small>{context}</small>}
      </button>
      {flag && (
        <span className={`tk-chip tp-d-flag is-${flag.tone}`}>
          {flag.label}
          {flag.date && ` · ${relativeDay(flag.date, today)}`}
        </span>
      )}
      {item.assignees.length > 0 && <BubbleStack users={users} emails={item.assignees} max={2} />}
    </li>
  );
};

/** A dashboard card: a heading with a count, the first few lines, and "show all". */
export const DashCard: React.FC<{
  title: string;
  icon: TaskIconName;
  count?: number;
  tone?: 'danger' | 'warn' | 'event' | 'normal';
  empty: string;
  wide?: boolean;
  children?: React.ReactNode;
  /** Lines to show before "Show all". */
  rows?: React.ReactNode[];
  show?: number;
}> = ({ title, icon, count, tone = 'normal', empty, wide, children, rows, show = 6 }) => {
  const [all, setAll] = useState(false);
  const list = rows || [];
  return (
    <section className={`tl-card tp-d-card is-${tone}${wide ? ' is-wide' : ''}`}>
      <header className="tp-d-head">
        <TaskIcon name={icon} size={15} />
        <h3>{title}</h3>
        {count !== undefined && count > 0 && <span className="tp-d-count">{count}</span>}
      </header>
      {children}
      {rows && (list.length ? (
        <>
          <ul className="tp-d-list">{all ? list : list.slice(0, show)}</ul>
          {list.length > show && (
            <button type="button" className="tl-section-toggle tp-d-more" onClick={() => setAll((v) => !v)}>
              <TaskIcon name="chevron-down" size={14} className={all ? '' : 'tl-rot'} />
              {all ? 'Show fewer' : `Show all ${list.length}`}
            </button>
          )}
        </>
      ) : (
        <p className="tl-empty">{empty}</p>
      ))}
    </section>
  );
};
